import { Topbar } from '../../components/Topbar/Topbar';
import { Alert, Badge, Button, ButtonGroup, Card, Container, ListGroup, Modal, ToggleButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faExclamationTriangle,
  faPaperclip,
  faThumbsDown as faThumbsDownFill,
  faThumbsUp as faThumbsUpFill,
  faUserTag,
} from '@fortawesome/free-solid-svg-icons';
import { Footer } from '../../components/Footer/Footer';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { formatSecondsTimestamp } from '../../utils/utils';
import './Ticket.scss';
import { TicketForm } from '../../components/TicketForm/TicketForm';
import { TicketStatusBadge } from '../../components/TicketStatusBadge/TicketStatusBadge';
import { faThumbsDown, faThumbsUp } from '@fortawesome/free-regular-svg-icons';
import { WithTooltip } from '../../components/WithTooltip/WithTooltip';
import { UserUID } from '../../components/UserUID/UserUID';
import { AdminNotes } from '../../components/AdminNotes/AdminNotes';
import { Copyable } from '../../components/Copyable/Copyable';
import { useTranslation } from 'react-i18next';
import { Title } from '../../components/Title/Title';

const TicketComponent = () => {
  const { getTicket, currentUser, rateTicket, assignTicket, requestTicketRating } = useFirebase();
  const [ticket, setTicket] = useState<any>();
  const [isAdmin, setIsAdmin] = useState(false);
  const [ticketRating, setTicketRating] = useState<1 | -1>(null);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const skipFirstRatingSubmit = useRef(true);
  const { t } = useTranslation();
  const [requestRatingModal, setRequestRatingModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) return history.replace('/');
    const asAdmin = window.location.pathname.startsWith('/admin/ticket/');
    setIsAdmin(asAdmin);
    getTicket(id, asAdmin).then((ticket) => {
      setTicket(ticket);
      setTicketRating(ticket.rating);
    });
  }, [id, isAdmin]);

  useEffect(() => {
    if (skipFirstRatingSubmit.current) {
      skipFirstRatingSubmit.current = false;
      return;
    }
    rateTicket(id, ticketRating).then(() => {});
  }, [ticketRating]);

  const requestRating = async () => {
    setRequestRatingModal(false);
    setLoading(true);
    await requestTicketRating(ticket.id, ticket.subject, ticket.uid);
    window.location.reload();
  };

  if (!ticket || loading) return <FullscreenLoader />;

  if (!Object.keys(ticket).length) return <Redirect to={'/'} />;

  return (
    <>
      <Title>{ticket.subject}</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <div className={'d-flex align-items-center'}>
          <Button variant={'outline-light'} onClick={() => history.goBack()} className={'mr-2'}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <div className={'display-4'}>{t('ticket.labels.dettaglioTicket')}</div>
        </div>

        <h3 className={'mt-4'}>{ticket.subject}</h3>
        <div className={'mt-4'}>
          <div className={'d-flex align-items-center'}>
            <span className={'mr-1'}>{t('ticket.labels.stato')}</span>
            <TicketStatusBadge status={ticket.status} />
          </div>
          <div className={'d-flex align-items-center'}>
            <span className={'mr-1'}>{t('ticket.labels.dataApertura')}</span>
            {formatSecondsTimestamp(ticket.created._seconds, 'dd/MM/Y HH:mm')}
          </div>
          {isAdmin && (
            <>
              <div className={'d-flex align-items-center'}>
                <span className={'mr-1'}>Ticket ID:</span>
                <Copyable text={ticket.id} />
              </div>
              <div className={'d-flex align-items-center'}>
                <span className={'mr-1'}>User ID:</span>
                <UserUID uid={ticket.uid} />
              </div>
            </>
          )}
          {(!!ticket.assignee || isAdmin) && (
            <div className={'d-flex align-items-center'}>
              <span className={'mr-1'}>{t('ticket.labels.assegnatoA')}</span>
              <div className={'font-weight-bold d-flex align-items-center ' + (!ticket.assignee ? 'text-warning' : '')}>
                {ticket.assignee || (
                  <span>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={'mr-1'} />
                    <span>{t('ticket.labels.nonAssegnato')}</span>
                  </span>
                )}
                {isAdmin && (
                  <>
                    {currentUser.uid != ticket.assigneeUid && (
                      <Button
                        className={'ml-2'}
                        size={'sm'}
                        disabled={assigneeLoading}
                        onClick={() => {
                          setAssigneeLoading(true);
                          assignTicket(id).then(() => {
                            getTicket(id, isAdmin).then((ticket) => {
                              setAssigneeLoading(false);
                              setTicket(ticket);
                            });
                          });
                        }}
                      >
                        {assigneeLoading ? t('labels.attendiDots') : 'Prendi in carico'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className={'text-right text-lg-center mt-3'}>
            <AdminNotes notes={ticket.notes} path={`tickets/${id}`} onSubmit={() => getTicket(id, isAdmin).then(setTicket)} context={['ticket', ticket.id]} />
          </div>
        )}

        <ListGroup className={'mt-4'}>
          {/*#2b4763*/}
          {ticket.messages.map((message, i) => (
            <Card key={message.id} bg={message.isStaff ? null : 'dark'} className={'mb-3'} style={{ backgroundColor: message.isStaff ? '#2b4763' : null }}>
              <Card.Header>
                <div className={'d-flex flex-column flex-lg-row align-items-start align-items-lg-center'}>
                  <div className={'d-flex flex-column flex-lg-row align-items-start align-items-lg-baseline'}>
                    <div className={'d-flex align-items-baseline'}>
                      <span className={'font-weight-bold'}>{message.author}</span>
                      {message.isStaff && (
                        <Badge pill variant={'info'} className={'ml-2 mr-1'}>
                          {t('labels.staff')}
                        </Badge>
                      )}
                    </div>
                    {message.isAssigneeChange && !message.statusChangedTo && <div className={'ml-0 ml-lg-1'}>{t('ticket.labels.haPresoInCarico')}</div>}

                    {!!message.statusChangedTo && (
                      <div className={message.isAssigneeChange ? '' : 'ml-0 ml-lg-1'}>
                        <span>{t('ticket.labels.haCambiatoIn')}</span>
                        <TicketStatusBadge status={message.statusChangedTo} className={'ml-1'} />
                      </div>
                    )}
                  </div>
                  <div className={'ml-auto d-flex align-items-center'}>
                    {message.isStaff && ticket.assigneeUid == message.uid && (
                      <span className={'mr-2 text-muted'}>
                        <WithTooltip text={'Assegnatario attuale'} big={false}>
                          <FontAwesomeIcon icon={faUserTag} size={'sm'} />
                        </WithTooltip>
                      </span>
                    )}
                    <small>{formatSecondsTimestamp(message.created._seconds, 'dd/MM/Y HH:mm')}</small>
                    {isAdmin && (
                      <small className={'ml-2'}>
                        <Copyable text={message.id} />
                      </small>
                    )}
                    <small className={'ml-2 text-muted'}>#{i + 1}</small>
                  </div>
                </div>
              </Card.Header>
              {!!message.message && <Card.Body>{message.message}</Card.Body>}
              {!!message.files?.length && (
                <Card.Footer>
                  <div className={'mb-2'}>
                    <small className={'font-weight-bold'}>
                      <FontAwesomeIcon icon={faPaperclip} className={'mr-1'} /> {t('ticket.labels.fileAllegati')}
                    </small>
                  </div>

                  <div className={'attachments-container'}>
                    {message.files.map((attachment, i) => (
                      <div
                        key={i.toString()}
                        className={'attachment'}
                        style={{ '--attachment-url': `url(${attachment})` } as any}
                        onClick={() => {
                          window.open(attachment, '_blank');
                        }}
                      />
                    ))}
                  </div>
                </Card.Footer>
              )}
            </Card>
          ))}
          {ticket.status.closed && (
            <div className={'border-top border-dark pt-2 text-center text-muted mb-3'}>
              {isAdmin ? "Il ticket è stato chiuso e l'utente non potrà aggiungere nuove risposte" : t('ticket.labels.utenteTicketChiuso')}
            </div>
          )}
          {ticket.messages.some((m) => m.isStaff) && (
            <>
              {!isAdmin && (
                <div className={'d-flex align-items-center justify-content-center'}>
                  <div className={'mr-2'}>{t('ticket.labels.servizioUtile')}</div>
                  <ButtonGroup toggle>
                    <ToggleButton
                      value={1}
                      type={'radio'}
                      variant={ticketRating == 1 ? 'success' : ticketRating == -1 ? 'outline-danger' : 'outline-light'}
                      name={'ticket-rating'}
                      checked={ticketRating == 1}
                      onChange={() => setTicketRating(1)}
                    >
                      <FontAwesomeIcon icon={ticketRating == 1 ? faThumbsUpFill : faThumbsUp} />
                    </ToggleButton>
                    <ToggleButton
                      value={-1}
                      type={'radio'}
                      variant={ticketRating == -1 ? 'danger' : ticketRating == 1 ? 'outline-success' : 'outline-light'}
                      name={'ticket-rating'}
                      checked={ticketRating == -1}
                      onChange={() => setTicketRating(-1)}
                    >
                      <FontAwesomeIcon icon={ticketRating == -1 ? faThumbsDownFill : faThumbsDown} />
                    </ToggleButton>
                  </ButtonGroup>
                </div>
              )}
              {isAdmin && (
                <div className={'text-center'}>
                  {!ticketRating && (
                    <span>
                      L'utente non ha valutato il servizio offerto.
                      <Button
                        size={'sm'}
                        className={'ml-2'}
                        onClick={() => setRequestRatingModal(true)}
                        variant={!!ticket.ratingsRequested ? 'warning' : 'primary'}
                      >
                        Richiedi valutazione
                      </Button>
                    </span>
                  )}
                  {ticketRating && (
                    <>
                      L'utente ha valutato
                      <span className={'font-weight-bold mx-1 ' + (ticketRating == 1 ? 'text-success' : 'text-danger')}>
                        {ticketRating == 1 ? 'POSITIVAMENTE' : 'NEGATIVAMENTE'}
                      </span>
                      il servizio offerto.
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </ListGroup>

        {(!ticket.status.closed || isAdmin) && (
          <TicketForm insideTicket={true} isAdmin={isAdmin} ticket={ticket} onSuccess={(id) => window.location.reload()} />
        )}
      </Container>

      <Footer />

      <Modal show={requestRatingModal} onHide={() => setRequestRatingModal(false)}>
        <Modal.Header>
          <Modal.Title>Richiedi valutazione ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tramite questa funzione, all'utente verrà inviata una mail che lo invita a valutare il servizio di assistenza che ha ricevuto per questo ticket.
          </p>
          <p>&Egrave; il caso di usare questa funzionalità con parsimonia, onde evitare spam nella casella dell'utente.</p>
          {!ticket.ratingsRequested && (
            <Alert variant={'success'} className={'mb-0'}>
              Per questo ticket non è mai stata richiesta la valutazione.
            </Alert>
          )}
          {!!ticket.ratingsRequested && (
            <Alert variant={'warning'} className={'mb-0'}>
              Per questo ticket è stata richiesta la valutazione <strong>{ticket.ratingsRequested}</strong> volt{ticket.ratingsRequested == 1 ? 'a' : 'e'}.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'secondary'} onClick={() => setRequestRatingModal(false)}>
            Annulla
          </Button>
          <Button variant={!!ticket.ratingsRequested ? 'warning' : 'primary'} onClick={() => requestRating()}>
            Invia richiesta di valutazione
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TicketComponent;
